import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { EventCreateRequest } from '@/api/bol';
import { JsonEditor, PrimaryButton, SecondaryButton } from '@/components/index';
import { defaultEventTemplate } from '@/utils/eventDefaultTemplate';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { MemberIdentifierSelect } from '../MemberIdentifierSelect';

interface EventsFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (eventData: EventCreateRequest, memberIdentifier: string) => Promise<void>;
}

interface FormData {
  memberIdentifier: string;
  eventJson: string;
}

const EventsFormDialog: React.FC<EventsFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { control, handleSubmit, formState: { isSubmitting, isValid, isSubmitted }, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      memberIdentifier: '',
      eventJson: defaultEventTemplate(),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        memberIdentifier: '',
        eventJson: defaultEventTemplate(),
      });
    }
  }, [open, reset]);

  const handleFormSubmit = async (data: FormData) => {
    let eventData: EventCreateRequest;
    try {
      eventData = parseJsonWithDates(data.eventJson);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      throw new Error('Invalid JSON format. Please check your JSON syntax.');
    }

    await onSubmit(eventData, data.memberIdentifier);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Event</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Controller
            name="memberIdentifier"
            control={control}
            rules={{ required: 'Member identifier is required' }}
            render={({ field, fieldState }) => (
              <MemberIdentifierSelect
                value={field.value}
                onChange={field.onChange}
                open={open}
                required
                error={isSubmitted ? fieldState.error?.message : undefined}
              />
            )}
          />
          <Controller
            name="eventJson"
            control={control}
            rules={{ 
              required: 'Event JSON is required', 
              validate: value => {
                try {
                  const parsed = JSON.parse(value);
                  if (!parsed.actualDateTime) return 'actualDateTime is required';
                  if (!parsed.code) return 'code is required';
                  if (!parsed.eventType) return 'eventType is required';
                  if (!parsed.domain) return 'domain is required';
                  return true;
                } catch {
                  return 'Invalid JSON format';
                }
              }
            }}
            render={({ field, fieldState }) => (
              <JsonEditor
                value={field.value}
                onChange={field.onChange}
                height="400px"
                label="Enter a valid Event JSON :"
                helperText="Optional: description, reasonCode, location, createdBy, accountableParty, documents."
                error={isSubmitted ? fieldState.error?.message : undefined}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default EventsFormDialog;
